import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useFeaturedProduct, useArchiveProducts, useScheduledProducts } from '@/hooks/useProducts'
import { FeaturedHero, FeaturedHeroEmpty } from '@/components/featured-hero'
import { ProductCard } from '@/components/product-card'
import { Button } from '@/components/ui/button'
import { CountdownTimer } from '@/components/countdown-timer'
import { Loader2, ArrowRight, Archive, Calendar, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { differenceInDays } from 'date-fns'
import { BRAND_DESCRIPTION, BRAND_NAME, DEFAULT_SITE_TITLE } from '@/lib/branding'

/**
 * "Next Drop" teaser component shown when no featured product but scheduled exists
 */
function NextDropTeaser({ startDate, className }: { startDate: string; className?: string }) {
  const start = new Date(startDate)
  const now = new Date()
  const daysUntil = differenceInDays(start, now)

  let timeLabel = 'Coming Soon'
  if (daysUntil <= 0) {
    timeLabel = 'Starting Today'
  } else if (daysUntil === 1) {
    timeLabel = 'Starts Tomorrow'
  } else {
    timeLabel = `Starts in ${daysUntil} Days`
  }

  return (
    <section
      className={cn(
        'relative flex items-center overflow-hidden bg-zinc-50 py-16 lg:py-24',
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-zinc-100 to-transparent" />
        <div className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-blue-100/40 blur-3xl" />
        <div className="absolute -right-24 bottom-8 h-72 w-72 rounded-full bg-zinc-200/60 blur-3xl" />
      </div>

      <div className="container relative mx-auto flex w-full px-4">
        <div className="mx-auto w-full max-w-2xl rounded-3xl border border-zinc-200/80 bg-white/85 p-8 text-center shadow-sm backdrop-blur md:p-12">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-600">
            <Calendar className="size-4" />
            Next Drop Scheduled
          </div>
          <h1 className="mb-4 text-3xl font-bold text-zinc-900 md:text-4xl lg:text-5xl">
            {timeLabel}
          </h1>
          <p className="mx-auto mb-6 max-w-xl text-lg text-zinc-600">
            A new featured deal is on the way. Check back soon or browse past reviews in the meantime.
          </p>

          {/* Countdown to next drop */}
          <div className="mb-8">
            <p className="mb-2 text-sm font-medium uppercase tracking-wider text-zinc-500">
              <Clock className="mr-1 inline size-4" />
              Countdown to Next Drop
            </p>
            <CountdownTimer
              targetDate={startDate}
              size="md"
              variant="light"
            />
          </div>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/past-reviews">Browse Past Reviews</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/about">How It Works</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export function HomePage() {
  const { product, isLoading, error } = useFeaturedProduct()
  const { products: archiveProducts } = useArchiveProducts()
  const { products: scheduledProducts } = useScheduledProducts()

  // Show just the first 3 archive items as preview
  const previewArchive = archiveProducts.slice(0, 3)
  const hasArchivePreview = previewArchive.length > 0

  // Get next scheduled drop (first in queue)
  const nextScheduled = scheduledProducts.length > 0 ? scheduledProducts[0] : null

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-red-400">Error loading featured product</p>
      </div>
    )
  }

  if (!product && !hasArchivePreview && !nextScheduled) {
    return (
      <>
        <Helmet>
          <title>{DEFAULT_SITE_TITLE}</title>
          <meta
            name="description"
            content={BRAND_DESCRIPTION}
          />
        </Helmet>

        <div className="flex flex-1 flex-col bg-zinc-50">
          <FeaturedHeroEmpty className="flex-1" />
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {product
            ? `${product.title} - ${product.featured_discount_percent}% Off This Week | ${BRAND_NAME}`
            : DEFAULT_SITE_TITLE
          }
        </title>
        <meta
          name="description"
          content={
            product
              ? product.short_description || product.description || ''
              : BRAND_DESCRIPTION
          }
        />
      </Helmet>

      {/* Featured Hero, Next Drop Teaser, or Empty State */}
      {product ? (
        <FeaturedHero product={product} />
      ) : nextScheduled?.featured_start_date ? (
        <NextDropTeaser startDate={nextScheduled.featured_start_date} />
      ) : (
        <FeaturedHeroEmpty />
      )}

      {/* Archive Preview Section */}
      {hasArchivePreview && (
        <section className="border-t border-zinc-200 bg-zinc-50 py-16">
          <div className="container mx-auto px-4">
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-2xl">
                <span className="mb-2 inline-flex items-center gap-2 text-sm font-medium text-zinc-500">
                  <Archive className="size-4" />
                  From the Archive
                </span>
                <h2 className="text-2xl font-bold text-zinc-900 md:text-3xl">Past Reviews</h2>
                <p className="mt-2 text-sm text-zinc-600 md:text-base">
                  Missed the featured week? Every past review stays available with ongoing savings.
                </p>
              </div>
              <Button asChild variant="outline" className="border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100">
                <Link to="/past-reviews">
                  Shop Past Reviews
                  <ArrowRight className="ml-1 size-4" />
                </Link>
              </Button>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {previewArchive.map(prod => (
                <ProductCard
                  key={prod.id}
                  product={prod}
                  variant="archive-compact"
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
